import { useTRPCAPI, useTRPCClientAPI, useTRPCMutation, useTRPCQuery } from '../../../../trpc/trpc';
import type { ImapSmtpInput, MailboxRow, MailboxTestResult } from '../outreachTypes';
import { useInvalidateOutreach } from './useInvalidateOutreach';
import { useRetry } from './useRetry';

/**
 * The mailboxes of the workspace (frame i1) and their writes. A mailbox belongs to the licence, so
 * the writes take no artist. `outreach.mailbox.updated` reads the list again.
 */
export function useMailboxes(accountId: number | null = null) {
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();
  const invalidate = useInvalidateOutreach();
  // With an artist: only the mailboxes that this artist may send from (the builder, frame e3).
  const query = useTRPCQuery(
    trpcAPI.outreach.mailboxes.list.queryOptions(accountId === null ? undefined : { accountId }, { retry: false })
  );
  const retry = useRetry(query.refetch);
  const done = () => invalidate(['mailboxes', 'home']);

  const test = useTRPCMutation({
    mutationFn: (input: ImapSmtpInput) =>
      trpcClient.outreach.mailboxes.testImapSmtp.mutate(input) as Promise<MailboxTestResult>,
  });
  // Any member connects (Task 8). With an artist, the new mailbox is for that artist.
  const connect = useTRPCMutation({
    mutationFn: (input: ImapSmtpInput) =>
      trpcClient.outreach.mailboxes.connectImapSmtp.mutate({ ...input, accountId }) as Promise<{ mailboxUuid: string }>,
    onSuccess: done,
  });
  // One sample pitch from the mailbox to the signed-in user (frame o7). It counts as no pitch.
  const sendTest = useTRPCMutation({
    mutationFn: (input: { mailboxUuid: string; campaignUuid?: string | null }) =>
      trpcClient.outreach.mailboxes.sendTest.mutate({
        mailboxUuid: input.mailboxUuid,
        accountId: input.campaignUuid === undefined || input.campaignUuid === null ? null : accountId,
        campaignUuid: input.campaignUuid ?? null,
      }) as Promise<{ ok: true; to: string } | { ok: false; reason: string; detail: string }>,
  });
  const pause = useTRPCMutation({
    mutationFn: (mailboxUuid: string) => trpcClient.outreach.mailboxes.pause.mutate({ mailboxUuid }),
    onSuccess: done,
  });
  const resume = useTRPCMutation({
    mutationFn: (mailboxUuid: string) => trpcClient.outreach.mailboxes.resume.mutate({ mailboxUuid }),
    onSuccess: done,
  });
  const remove = useTRPCMutation({
    mutationFn: (mailboxUuid: string) => trpcClient.outreach.mailboxes.remove.mutate({ mailboxUuid }),
    onSuccess: done,
  });

  return {
    mailboxes: (query.data as MailboxRow[] | undefined) ?? [],
    isLoading: query.isLoading,
    error: query.error,
    retry,
    testImapSmtp: test.mutateAsync,
    isTesting: test.isPending,
    connectImapSmtp: connect.mutateAsync,
    isConnecting: connect.isPending,
    sendTest: sendTest.mutateAsync,
    isSendingTest: sendTest.isPending,
    pauseMailbox: pause.mutateAsync,
    resumeMailbox: resume.mutateAsync,
    removeMailbox: remove.mutateAsync,
    busyUuid:
      (pause.isPending
        ? pause.variables
        : resume.isPending
          ? resume.variables
          : remove.isPending
            ? remove.variables
            : null) ?? null,
  };
}
